import { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { ItemModel } from 'common/components/DropDownButton/ItemModel';
import {
  useMutationDeleteInvoice,
  useMutationFiscalizationF1,
  useMutationFiscalizationF2,
  useMutationMarkAsPaidInvoice,
  useMutationPrintingAndLoggingOfInvoices,
  useMutationSendInvoiceToEmail,
  useMutationStornInvoice,
  useQueryMarkAsPaidInvoice,
  useQueryReviewInvoice,
  useQuerySendInvoiceToEmail,
} from 'modules/_common/hooks';
import { IInvoices } from 'modules/_common/models';

interface IInvoiceCombined extends ItemModel {
  invoice: IInvoices;
}

export type TInvoiceState = {
  invoicesState: IInvoices[];
  invoiceDetails: IInvoices | null;
  isStornModalVisible: boolean;
  isInvoicePdfReviewModalVisible: boolean;
  isPrintingAndLoggingOfInvoicesModalVisible: boolean;
  isFiscalizationF1PdfModalVisible: boolean;
  isFiscalizationF2PdfModalVisible: boolean;
  isMarkAsPaidModalVisible: boolean;
  isSendToEmailVisible: boolean;
  isCreateInvoiceModalVisible: boolean;
  isDeleteInvoiceModalVisible: boolean;
};

export const useInvoiceActions = ({ invoices }: { invoices: IInvoices[] | undefined }) => {
  const { t } = useTranslation();

  const { colors } = useTheme();

  const INITIAL_VALUES: TInvoiceState = {
    invoicesState: [],
    invoiceDetails: null,
    isStornModalVisible: false,
    isInvoicePdfReviewModalVisible: false,
    isPrintingAndLoggingOfInvoicesModalVisible: false,
    isFiscalizationF1PdfModalVisible: false,
    isFiscalizationF2PdfModalVisible: false,
    isMarkAsPaidModalVisible: false,
    isSendToEmailVisible: false,
    isCreateInvoiceModalVisible: false,
    isDeleteInvoiceModalVisible: false,
  };

  const [invoiceState, setInvoiceState] = useReducer(
    (state: TInvoiceState, newState: Partial<TInvoiceState>): TInvoiceState => ({
      ...state,
      ...newState,
    }),
    INITIAL_VALUES,
  );

  const {
    invoicesState,
    invoiceDetails,
    isStornModalVisible,
    isInvoicePdfReviewModalVisible,
    isPrintingAndLoggingOfInvoicesModalVisible,
    isFiscalizationF1PdfModalVisible,
    isFiscalizationF2PdfModalVisible,
    isMarkAsPaidModalVisible,
    isSendToEmailVisible,
    isCreateInvoiceModalVisible,
    isDeleteInvoiceModalVisible,
  } = invoiceState;

  const {
    data: reviewInvoiceData,
    isLoading: isLoadingReviewInvoice,
    isError: isErrorReviewInvoice,
    error: errorReviewInvoice,
    isFetching,
  } = useQueryReviewInvoice({
    invoiceId: String(invoiceDetails?.invoiceId),
    enabled: isInvoicePdfReviewModalVisible,
  });

  const {
    data: markAsPaidInvoiceData,
    isLoading: isLoadingMarkAsPaidInvoice,
    isError: isErrorMarkAsPaidInvoice,
    error: errorMarkAsPaidInvoice,
    isFetching: isFetchingMarkAsPaidInvoice,
  } = useQueryMarkAsPaidInvoice({
    invoiceId: String(invoiceDetails?.invoiceId),
    enabled: isMarkAsPaidModalVisible,
  });

  const {
    data: invoiceEmailData,
    isLoading: isLoadingInvoiceEmailData,
    isError: isErrorInvoiceEmailData,
    error: errorInvoiceEmailData,
    isFetching: isFetchingInvoiceEmailData,
  } = useQuerySendInvoiceToEmail({
    invoiceId: String(invoiceDetails?.invoiceId),
    enabled: isSendToEmailVisible,
  });

  /* Mutations */
  const {
    mutateAsync: printingAndLoggingOfInvoices,
    data: pdfPrintingAndLoggingData,
    isLoading: isLoadingPrintingAndLoggingOfInvoices,
    isError: isErrorPrintingAndLoggingOfInvoices,
  } = useMutationPrintingAndLoggingOfInvoices();

  const {
    mutate: fiscalizationF1,
    data: fiscalizationF1Data,
    isLoading: isLoadingFiscalizationF1,
    isError: isErrorFiscalizationF1,
  } = useMutationFiscalizationF1();

  const {
    mutate: fiscalizationF2,
    data: fiscalizationF2Data,
    isLoading: isLoadingFiscalizationF2,
    isError: isErrorFiscalizationF2,
  } = useMutationFiscalizationF2();

  const {
    mutateAsync: updateMarkAsPaidInvoice,
    isLoading: isLoadingUpdateMarkAsPaidInvoice,
    isError: isErrorUpdateMarkAsPaidInvoice,
  } = useMutationMarkAsPaidInvoice();

  const {
    mutateAsync: sendInvoiceToEmail,
    isLoading: isLoadingSendInvoiceToEmail,
    isError: isErrorSendInvoiceToEmail,
  } = useMutationSendInvoiceToEmail();

  const {
    mutateAsync: stornInvoice,
    isLoading: isLoadingStornInvoice,
    isError: isErrorStornInvoice,
  } = useMutationStornInvoice();

  const {
    mutateAsync: deleteInvoice,
    isLoading: isLoadingDeleteInvoice,
    isError: isErrorDeleteInvoice,
  } = useMutationDeleteInvoice();

  useEffect(() => {
    const invoicesOptions = [
      {
        label: t('Edit'),
        onClick: async (item: IInvoiceCombined) => {
          setInvoiceState({ invoiceDetails: item.invoice, isCreateInvoiceModalVisible: true });
        },
      },
      {
        label: t('Delete'),
        onClick: async (item: IInvoiceCombined) => {
          setInvoiceState({ invoiceDetails: item.invoice, isDeleteInvoiceModalVisible: true });
        },
      },
      {
        label: t('Review'),
        onClick: async (item: IInvoiceCombined) => {
          setInvoiceState({
            isInvoicePdfReviewModalVisible: true,
            invoiceDetails: item.invoice,
          });
        },
      },
      {
        label: t('Print the bill'),
        onClick: async (item: IInvoiceCombined) => {
          setInvoiceState({ isPrintingAndLoggingOfInvoicesModalVisible: true });
          printingAndLoggingOfInvoices({
            invoiceId: item.invoice.invoiceId,
            isPrintedAndLogged: false,
          });
        },
      },
      {
        label: t('Print and log the bill'),
        onClick: async (item: IInvoiceCombined) => {
          setInvoiceState({ isPrintingAndLoggingOfInvoicesModalVisible: true });
          printingAndLoggingOfInvoices({
            invoiceId: item.invoice.invoiceId,
            isPrintedAndLogged: true,
          });
        },
      },
      {
        label: t('Fiscalization'),
        itemDropdown: [
          {
            label: t('Fiscalization 1.0'),
            onClick: (item: IInvoiceCombined) => {
              setInvoiceState({ isFiscalizationF1PdfModalVisible: true });
              fiscalizationF1({ invoiceId: item.invoice.invoiceId });
            },
          },
          {
            label: t('Send e-invoice'),
            onClick: (item: IInvoiceCombined) => {
              setInvoiceState({ isFiscalizationF2PdfModalVisible: true });
              fiscalizationF2({ invoiceId: item.invoice.invoiceId });
            },
          },
          /* {
            label: t('Show ubl file'),
            onClick: (item: IInvoiceCombined) => console.log('Show ubl file', item),
          }, */
        ],
      },
      {
        label: t('Mark as paid'),
        onClick: (item: IInvoiceCombined) => {
          setInvoiceState({
            isMarkAsPaidModalVisible: true,
            invoiceDetails: item.invoice,
          });
        },
      },
      {
        label: t('Send to email'),
        onClick: (item: IInvoiceCombined) =>
          setInvoiceState({ isSendToEmailVisible: true, invoiceDetails: item.invoice }),
      },
      {
        label: t('Storn'),
        onClick: (item: IInvoiceCombined) => {
          setInvoiceState({ isStornModalVisible: true, invoiceDetails: item.invoice });
        },
        fontColorItem: colors.negative_dark,
      },
    ];

    if (invoices) {
      const newInvoicesState = invoices?.map(invoice => {
        const { fiscalizationStatus, invoiceStatusDescription, reservationId, invoiceStatus } =
          invoice;

        // Ako ručno kreiran račun(!reservationId) nije fiskaliziran i fiscalizationStatus === "Nije fiskalizirano" ili račun ima vezu na rezervaciju(reservationId) i fiscalizationStatus === Nije fiskalizirano && invoiceStatusDescription === "Radni"

        const canEditOrDelete =
          (invoiceStatus === '1' || invoiceStatus === '-1') &&
          fiscalizationStatus === 'Nije fiskalizirano' &&
          (!reservationId || invoiceStatusDescription === 'Radni');

        const canStorn = invoiceStatus === '2' && fiscalizationStatus !== 'Stornirano';

        return {
          ...invoice,
          invoicesOptions: invoicesOptions?.map(invoiceActionBtn => {
            return {
              ...invoiceActionBtn,
              invoice,
              itemDropdown: invoiceActionBtn.itemDropdown?.map(invoiceOptionDropdown => {
                return { ...invoiceOptionDropdown, invoice };
              }),
              disabled:
                ((invoiceActionBtn.label === t('Edit') || invoiceActionBtn.label === t('Delete')) &&
                  !canEditOrDelete) ||
                (invoiceActionBtn.label === t('Storn') && !canStorn),
            };
          }),
        };
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setInvoiceState({ invoicesState: newInvoicesState });
    }
  }, [invoices]);

  return {
    invoicesState,
    invoiceDetails,
    setInvoiceState,
    isStornModalVisible,
    isCreateInvoiceModalVisible,
    stornInvoice,
    isInvoicePdfReviewModalVisible,
    isLoading:
      isLoadingReviewInvoice ||
      isFetching ||
      isLoadingPrintingAndLoggingOfInvoices ||
      isLoadingFiscalizationF1 ||
      isLoadingFiscalizationF2 ||
      isLoadingMarkAsPaidInvoice ||
      isFetchingMarkAsPaidInvoice ||
      isLoadingUpdateMarkAsPaidInvoice ||
      isLoadingInvoiceEmailData ||
      isFetchingInvoiceEmailData ||
      isLoadingSendInvoiceToEmail ||
      isLoadingStornInvoice ||
      isLoadingDeleteInvoice,
    isError:
      isErrorReviewInvoice ||
      isErrorFiscalizationF1 ||
      isErrorPrintingAndLoggingOfInvoices ||
      isErrorFiscalizationF2 ||
      isErrorMarkAsPaidInvoice ||
      isErrorUpdateMarkAsPaidInvoice ||
      isErrorSendInvoiceToEmail ||
      isErrorStornInvoice ||
      isErrorInvoiceEmailData ||
      isErrorDeleteInvoice,
    error: errorReviewInvoice || errorMarkAsPaidInvoice || errorInvoiceEmailData,
    reviewInvoiceData,
    pdfPrintingAndLoggingData,
    isPrintingAndLoggingOfInvoicesModalVisible,
    fiscalizationF1Data,
    isFiscalizationF1PdfModalVisible,
    fiscalizationF2Data,
    isFiscalizationF2PdfModalVisible,
    isMarkAsPaidModalVisible,
    markAsPaidInvoiceData,
    updateMarkAsPaidInvoice,
    isSendToEmailVisible,
    invoiceEmailData,
    sendInvoiceToEmail,
    isDeleteInvoiceModalVisible,
    deleteInvoice,
  };
};
